// SCR-CONSOLE-003 — ایجاد مستأجر (US-PLT-001): نام حقوقی و نمایشی، نوع مرکز، شناسه عمومی با بررسی زنده الگو و یکتایی، پلن
// (نسخه منتشرشده)، موبایل و کد ملی مالک، نوع مالکیت پیش‌فرض و is_demo؛ پس از ثبت ← جزئیات با Polling وضعیت Provisioning.
import { zodResolver } from '@hookform/resolvers/zod';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type CenterType, type CreateTenantRequest, type OwnershipType } from '@phoenix/api-client';
import { useIdempotencyKey, usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  FormPage,
  MobileFieldRHF,
  NationalIdFieldRHF,
  SelectRHF,
  TextFieldRHF,
  zMobile,
  zNationalId,
  zRequiredText,
  type SelectOption,
  faDigits,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { consoleKeys, publishedVersions, toNumber } from '../../api';
import { validateSlug, zSlug } from '../../slug';

export const SCR_CONSOLE_003 = 'SCR-CONSOLE-003';

export const CENTER_TYPES: readonly CenterType[] = ['Office', 'Clinic', 'Polyclinic', 'Chain'];
export const OWNERSHIP_TYPES: readonly OwnershipType[] = [
  'Private',
  'Governmental',
  'Charity',
  'PublicNonGovernmental',
];

const schema = z.object({
  legalName: zRequiredText(200),
  displayName: z.string().max(200, { error: 'common:validation.too_long' }),
  centerType: z.enum(['Office', 'Clinic', 'Polyclinic', 'Chain'], { error: 'common:validation.required' }),
  slug: zSlug(),
  plan: z.string().min(1, { error: 'common:validation.required' }),
  ownerMobile: zMobile(),
  ownerNationalId: z.union([z.literal(''), zNationalId()]),
  defaultOwnershipType: z.enum(['Private', 'Governmental', 'Charity', 'PublicNonGovernmental']),
  isDemo: z.boolean(),
});

type FormInput = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

const FIELDS = [
  'legalName',
  'displayName',
  'centerType',
  'slug',
  'plan',
  'ownerMobile',
  'ownerNationalId',
  'defaultOwnershipType',
  'isDemo',
] as const;

type SlugAvailability = 'idle' | 'checking' | 'available' | 'taken';

/** بررسی زنده یکتایی شناسه عمومی با جستجوی Console (تطبیق دقیق؛ تصمیم نهایی ایندکس یکتای سرور — C-AGENT-204). */
function useSlugAvailability(slug: string): SlugAvailability {
  const { api } = usePlatformSession();
  const [debounced, setDebounced] = useState(slug);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(slug.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [slug]);
  const valid = validateSlug(debounced) === null;
  const query = useQuery({
    queryKey: ['platform', 'console', 'slug-check', debounced],
    queryFn: () =>
      unwrap(api.GET('/api/v1/platform/console/tenants', { params: { query: { q: debounced, limit: 50 } } })),
    enabled: valid,
    staleTime: 0,
    retry: false,
  });
  if (!valid || debounced !== slug.trim()) {
    return valid && debounced !== slug.trim() ? 'checking' : 'idle';
  }
  if (query.isPending) {
    return 'checking';
  }
  if (!query.data) {
    return 'idle';
  }
  return query.data.items.some((item) => item.slug === debounced) ? 'taken' : 'available';
}

export function TenantCreatePage() {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(undefined);

  const plans = useQuery({
    queryKey: consoleKeys.plans,
    queryFn: () => unwrap(api.GET('/api/v1/platform/console/plans')),
  });

  const planOptions = useMemo<SelectOption[]>(
    () =>
      (plans.data?.items ?? []).flatMap((plan) =>
        publishedVersions(plan).map((version) => ({
          value: `${plan.code}@${toNumber(version.version) ?? 0}`,
          label: t('console:tenants.create.plan_option', {
            code: plan.code,
            version: faDigits(toNumber(version.version) ?? 0),
          }),
        })),
      ),
    [plans.data, t],
  );

  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      legalName: '',
      displayName: '',
      centerType: '' as unknown as CenterType,
      slug: '',
      plan: '',
      ownerMobile: '',
      ownerNationalId: '',
      defaultOwnershipType: 'Private',
      isDemo: false,
    },
  });
  const { control, handleSubmit, setError, formState, reset } = form;
  const slug = useWatch({ control, name: 'slug' }) ?? '';
  const availability = useSlugAvailability(slug);

  const create = useMutation({
    mutationFn: (body: CreateTenantRequest) =>
      unwrap(
        api.POST('/api/v1/platform/console/tenants', {
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (tenant, _variables) => {
      queryClient.setQueryData(consoleKeys.tenant(tenant.id), tenant);
      void queryClient.invalidateQueries({ queryKey: consoleKeys.tenantsRoot });
      queryClient.removeQueries({ queryKey: ['platform', 'console', 'slug-check'] });
      reset(form.getValues());
      setCreatedId(tenant.id);
    },
    onMutate: () => setSubmitError(undefined),
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: FIELDS,
        aliases: { planCode: 'plan', planVersion: 'plan' },
        codeFields: { 'PHX-PLT-0101': 'slug', 'PHX-PLT-0104': 'plan' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  // ناوبری پس از Reset فرم (DirtyGuard دیگر جلوی آن را نمی‌گیرد).
  useEffect(() => {
    if (createdId && !formState.isDirty) {
      void navigate(`/tenants/${createdId}`, { state: { created: true } });
    }
  }, [createdId, formState.isDirty, navigate]);

  const onSubmit = handleSubmit((values) => {
    const [planCode, planVersion] = values.plan.split('@');
    create.mutate({
      slug: values.slug.trim(),
      legalName: values.legalName,
      displayName: values.displayName.trim() || null,
      centerType: values.centerType,
      planCode: planCode ?? null,
      planVersion: planVersion ? Number(planVersion) : null,
      ownerMobile: values.ownerMobile,
      ownerNationalId: values.ownerNationalId || null,
      defaultOwnershipType: values.defaultOwnershipType,
      isDemo: values.isDemo,
    });
  });

  const fieldError = form.formState.errors.slug;
  const slugHelper =
    !fieldError && availability === 'taken' ? (
      <Stack direction="row" spacing={0.5} alignItems="center" component="span" data-slug-state="taken">
        <ErrorOutlineIcon fontSize="inherit" color="error" />
        <span>{t('console:tenants.create.slug_taken')}</span>
      </Stack>
    ) : !fieldError && availability === 'available' ? (
      <Stack direction="row" spacing={0.5} alignItems="center" component="span" data-slug-state="available">
        <CheckCircleOutlineIcon fontSize="inherit" color="success" />
        <span>{t('console:tenants.create.slug_available')}</span>
      </Stack>
    ) : null;

  return (
    <FormPage
      screenId={SCR_CONSOLE_003}
      titleKey="console:tenants.create.title"
      subtitleKey="console:tenants.create.subtitle"
      onSubmit={() => void onSubmit()}
      isDirty={formState.isDirty}
      isSubmitting={create.isPending}
      submitError={submitError}
      isLoading={plans.isPending}
      loadError={plans.error}
      onCancel={() => void navigate('/tenants')}
      submitKey="console:tenants.create.submit"
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextFieldRHF
          control={control}
          name="legalName"
          labelKey="console:tenants.create.legal_name"
          required
          fullWidth
          autoFocus
        />
        <TextFieldRHF
          control={control}
          name="displayName"
          labelKey="console:tenants.create.display_name"
          fullWidth
        />
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <SelectRHF
          control={control}
          name="centerType"
          labelKey="console:tenants.create.center_type"
          required
          fullWidth
          options={CENTER_TYPES.map((value) => ({ value, labelKey: `console:center_type.${value}` }))}
        />
        <div style={{ width: '100%' }}>
          <TextFieldRHF
            control={control}
            name="slug"
            labelKey="console:tenants.create.slug"
            helperKey="console:tenants.create.slug_help"
            required
            fullWidth
            slotProps={{ htmlInput: { dir: 'ltr', autoComplete: 'off', 'data-testid': 'slug-input' } }}
          />
          {slugHelper && (
            <Typography
              variant="caption"
              component="div"
              sx={{ mt: 0.5 }}
              data-testid="slug-availability"
              data-slug-state={availability}
              role="status"
            >
              {slugHelper}
            </Typography>
          )}
        </div>
      </Stack>
      <SelectRHF
        control={control}
        name="plan"
        labelKey="console:tenants.create.plan"
        helperKey="console:tenants.create.plan_help"
        required
        fullWidth
        options={planOptions}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <MobileFieldRHF
          control={control}
          name="ownerMobile"
          labelKey="console:tenants.create.owner_mobile"
          required
          fullWidth
        />
        <NationalIdFieldRHF
          control={control}
          name="ownerNationalId"
          labelKey="console:tenants.create.owner_national_id"
          fullWidth
        />
      </Stack>
      <SelectRHF
        control={control}
        name="defaultOwnershipType"
        labelKey="console:tenants.create.default_ownership_type"
        helperKey="console:tenants.create.default_ownership_help"
        fullWidth
        options={OWNERSHIP_TYPES.map((value) => ({ value, labelKey: `console:ownership_type.${value}` }))}
      />
      <CheckboxRHF
        control={control}
        name="isDemo"
        labelKey="console:tenants.create.is_demo"
        helperKey="console:tenants.create.is_demo_help"
      />
    </FormPage>
  );
}
